import { gql, useQuery } from '@apollo/client';
import IconSupport from '@apollo/icons/large/IconSupport.svg';
import {
  Button,
  ButtonGroup,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  StatusBadge,
  StatusBadgeProps,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import moment from 'moment';
import React, { useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { ContactSupportButton } from 'src/components/ContactSupportCTAs';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import type { TicketPriority } from 'src/lib/graphqlTypes/types';
import { safeCompileMarkdown } from 'src/lib/safeCompileMarkdown';

import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from './ChampionDashboardCard';
import { EmptyLayout } from './EmptyLayout';

const STATUS_COLORS = {
  P0: 'red',
  P1: 'red',
  P2: 'yellow',
  P3: 'green',
  P4: 'green',
} satisfies Record<TicketPriority, StatusBadgeProps['colorScheme']>;

export const SupportTicketsWidget = ({ accountId }: { accountId: string }) => {
  const [activeTicketId, setActiveTicketId] = useState<number | null>(null);
  const { data, loading, error } = useQuery<
    GraphQLTypes.ChampionDashboard_SupportTicketsWidget,
    GraphQLTypes.ChampionDashboard_SupportTicketsWidgetVariables
  >(
    gql`
      query ChampionDashboard_SupportTicketsWidget($accountId: ID!) {
        account(id: $accountId) {
          id
          name
          supportTickets {
            id
            summary
            priority
            created
          }
        }
      }
    `,
    { pollInterval: 60_000, variables: { accountId } },
  );

  const { data: ticketData, loading: loadingTicket } = useQuery<
    GraphQLTypes.SupportTicketsWidgetTicketQuery,
    GraphQLTypes.SupportTicketsWidgetTicketQueryVariables
  >(
    gql`
      query SupportTicketsWidgetTicketQuery($accountId: ID!, $ticketId: ID!) {
        account(id: $accountId) {
          id
          supportTicket(id: $ticketId) {
            id
            description
            priority
            created
            graph {
              id
              title
            }
            user {
              id
              fullName
              email
            }
          }
        }
      }
    `,
    {
      variables: { accountId, ticketId: String(activeTicketId) },
      skip: !activeTicketId,
    },
  );

  const tickets = data?.account?.supportTickets ?? [];
  const activeTicket = ticketData?.account?.supportTicket;

  const priorities = Config.jsmSupportTicketPriorities;

  return (
    <>
      <ChampionDashboardCard className="col-span-full lg:col-span-3 xl:col-span-2">
        <CardHeader>
          <CardTitle>Support tickets</CardTitle>
          {tickets.length > 0 && (
            <CardDescription>
              There {tickets.length === 1 ? 'is' : 'are'} a total of{' '}
              <Text as="span" variant="bodySb" className="text-secondary">
                {tickets.length} open{' '}
                {format.pluralize(tickets.length, 'ticket')}
              </Text>
            </CardDescription>
          )}
          <ContactSupportButton variant="secondary">
            Contact us
          </ContactSupportButton>
        </CardHeader>
        <ChampionDashboardCardBody>
          {error ? (
            <div>
              Oops.. something went wrong, click{' '}
              <a
                href="https://support.apollographql.com/user/requests?reporter=ALL_REQUESTS"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                here
              </a>{' '}
              to view your open Support tickets
            </div>
          ) : loading ? (
            <Loading />
          ) : tickets.length > 0 ? (
            <TableContainer>
              <Table isInteractive size="condensed">
                <Thead>
                  <Tr>
                    <Th>Open tickets</Th>
                    <Th>Priority</Th>
                    <Th>Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tickets.map((ticket) => (
                    <Tr
                      key={ticket.id}
                      onClick={() => setActiveTicketId(ticket.id)}
                      className="cursor-pointer"
                    >
                      <Td className="w-full max-w-0">
                        <div className="truncate" title={ticket.summary}>
                          {ticket.summary}
                        </div>
                      </Td>
                      <Td>
                        <StatusBadge
                          colorScheme={STATUS_COLORS[ticket.priority]}
                          variant="hidden"
                        >
                          {priorities[ticket.priority].label}
                        </StatusBadge>
                      </Td>
                      <Td>{moment(ticket.created).format('M/D/YY')}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyLayout
              icon={<IconSupport />}
              body="You have no support tickets open. Our customer support team is always happy to assist with any account or technical issues."
            />
          )}
        </ChampionDashboardCardBody>
      </ChampionDashboardCard>

      {activeTicketId !== null && (
        <Modal isOpen size="xl" onClose={() => setActiveTicketId(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {data?.account?.name}: {activeTicket?.graph?.title}
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              {loadingTicket ? (
                <Loading />
              ) : !activeTicket ? null : (
                <>
                  <FormControl>
                    <FormLabel>Reporter</FormLabel>
                    <Text>
                      {activeTicket.user?.fullName} ({activeTicket?.user?.email}
                      )
                    </Text>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Date</FormLabel>
                    <Text>{moment(activeTicket.created).format('M/D/YY')}</Text>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Priority</FormLabel>
                    <StatusBadge
                      colorScheme={STATUS_COLORS[activeTicket.priority]}
                      variant="hidden"
                    >
                      {priorities[activeTicket.priority].label}
                    </StatusBadge>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Body</FormLabel>
                    <Text>{safeCompileMarkdown(activeTicket.description)}</Text>
                  </FormControl>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  onClick={() => setActiveTicketId(null)}
                >
                  Close
                </Button>
                <ContactSupportButton variant="primary">
                  Contact us
                </ContactSupportButton>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
